import React, { useState } from 'react';
import { Menu, MenuItem, MenuSeparator } from '../Menu';
import Button from '../Button';
import Unread from '../Unread';
import Field from '../Field';
import { messageTemplateLabelMax, messageTemplateRefusal, composeWord } from '../../domain/compose';
import { templatePreviewLine } from '../../domain/noteTemplates';
import s from './drawers.module.css';
import c from './composer.module.css';

// New Message's `Templates ▾` (#597), MMS's: `Save New Template`, then the
// saved ones by name. Picking one puts its words into the composer — appended
// to the message, and its subject, when it has one, in place of the typed one
// (`apply_template` in core/src/compose.rs, #605). Save New Template asks for a
// name and keeps the composer's subject and message under it. No Delete here,
// as in MMS's menu: the list is managed under Business Settings → Email & SMS
// → Custom Email Templates (#608, src/components/settings/CustomEmailTemplates.jsx).
export default function MessageTemplatesMenu({ templates, composer }) {
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [label, setLabel] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const startSaving = () => {
    setOpen(false);
    setLabel('');
    setError(null);
    setNaming(true);
  };

  const save = () => {
    const refusal = messageTemplateRefusal(label, composer.body);
    if (refusal) {
      setError(refusal.message);
      return;
    }
    setSaving(true);
    templates.saveTemplate({ label, subject: composer.subject, body: composer.body }).then(
      () => {
        setSaving(false);
        setNaming(false);
      },
      (err) => {
        setSaving(false);
        setError(err?.message || String(err));
      },
    );
  };

  return (
    <>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        align="left"
        trigger={(
          <button
            type="button"
            className={c.tool}
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            {composeWord('templates')} ▾
          </button>
        )}
      >
        <MenuItem onClick={startSaving}>{composeWord('save_template')}</MenuItem>
        <MenuSeparator />
        {/* Unread is not "No templates saved yet." (#629). */}
        {templates.templates.length === 0 && !templates.loaded ? (
          <Unread failed={templates.failed} />
        ) : templates.templates.length === 0 ? (
          <div className={c.templatesEmpty}>{composeWord('templates_empty')}</div>
        ) : templates.templates.map((t) => (
          <button
            key={t.id}
            type="button"
            className={c.templatePick}
            onClick={() => { setOpen(false); composer.pickTemplate(t); }}
          >
            <span className={c.templateLabel}>{t.label}</span>
            {/* The subject, else the message's first line: two templates
                called "Welcome" are told apart by what is in them. */}
            <span className={c.templatePreview}>{t.subject || templatePreviewLine(t.body)}</span>
          </button>
        ))}
      </Menu>
      {naming ? (
        <div className={c.saveForm}>
          <Field label={composeWord('template_name')} error={error}>
            <input
              className={s.input}
              value={label}
              maxLength={messageTemplateLabelMax()}
              onChange={(e) => { setLabel(e.target.value); setError(null); }}
            />
          </Field>
          <div className={c.saveActions}>
            <Button size="sm" loading={saving} onClick={save}>{composeWord('template_save')}</Button>
            <Button size="sm" variant="secondary" onClick={() => setNaming(false)}>{composeWord('template_cancel')}</Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
